import { openExceptions } from "@/lib/finance";
import type { Case, CaseRecord, CaseStage, InboxTask, TaskUrgency } from "@/lib/types";
import { readCase, readCases } from "./store";

export async function listCases(): Promise<Case[]> {
  return (await readCases()).map((record) => record.case);
}

export async function getCase(caseId: string): Promise<Case | null> {
  return (await readCase(caseId))?.case ?? null;
}

export async function getCaseRecord(caseId: string): Promise<CaseRecord | null> {
  return readCase(caseId);
}

export async function listCasesForBorrower(borrowerId: string): Promise<Case[]> {
  return (await listCases()).filter((entry) => entry.borrowerId === borrowerId);
}

const STAGE_ORDER: Record<CaseStage, number> = {
  approval: 0,
  underwriting: 1,
  data_collection: 2,
  approved: 3,
  rejected: 4,
  closed: 5,
};

/**
 * A decided case is finished work. It stays on the borrower profile and keeps
 * its audit trail, but it leaves the inbox — otherwise approving a case would
 * leave it sitting in the queue it was meant to clear.
 */
const OPEN_STAGES: ReadonlySet<CaseStage> = new Set<CaseStage>([
  "data_collection",
  "underwriting",
  "approval",
]);

/**
 * The inbox answers "what needs me next", so a case contributes the single
 * task that is actually blocking it — not one row per open item.
 */
export async function listInboxTasks(): Promise<InboxTask[]> {
  const records = await readCases();
  const tasks = records
    .filter((record) => OPEN_STAGES.has(record.case.stage))
    .map((record) => toTask(record));
  return tasks.sort((a, b) => {
    const byStage = STAGE_ORDER[a.stage] - STAGE_ORDER[b.stage];
    return byStage !== 0 ? byStage : b.waitingDays - a.waitingDays;
  });
}

const URGENCY: Record<TaskUrgency, number> = { blocking: 0, attention: 1, routine: 2 };

export function compareUrgency(a: TaskUrgency, b: TaskUrgency): number {
  return URGENCY[a] - URGENCY[b];
}

function toTask(record: CaseRecord): InboxTask {
  const { case: kase, evidence } = record;
  const missing = evidence.find((source) => source.status === "missing");
  const exceptions = openExceptions(evidence);
  const unresolved = kase.openQuestions.filter((question) => !question.resolved);

  let task: string;
  let urgency: TaskUrgency;
  let target: InboxTask["target"];

  if (kase.stage === "approval") {
    task = "Ready for credit decision";
    urgency = "attention";
    target = "decision";
  } else if (missing) {
    task = `Missing ${missing.name.replace(/ — .*/, "").toLowerCase()}`;
    urgency = "blocking";
    target = "evidence";
  } else if (exceptions.length > 0) {
    task = unresolved[0]?.question ?? `Resolve ${exceptions.length} open exceptions`;
    urgency = "attention";
    target = unresolved[0]?.target.tab ?? "evidence";
  } else {
    task = "Review and progress";
    urgency = "routine";
    target = "overview";
  }

  return {
    id: `task-${kase.id}`,
    caseId: kase.id,
    borrowerName: kase.borrowerName,
    task,
    amount: kase.requestedAmount,
    stage: kase.stage,
    urgency,
    waitingDays: daysSince(kase.openedAt),
    target,
  };
}

/** Deterministic: measured against the fixed "today" the mock data is set in. */
const TODAY = Date.parse("2026-08-24T15:00:00Z");

function daysSince(iso: string): number {
  return Math.max(0, Math.floor((TODAY - Date.parse(iso)) / 86_400_000));
}
